import React, { useCallback, useEffect, useState } from "react"
import { accessFromProps } from "../lib/book-access"
import { BookShell } from "../components/book-shell"
import { PageHeader } from "../components/page-header"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { CurrencySearchSelect } from "../components/currency-search-select"
import { CURRENCIES } from "../lib/currencies"
import { appConfirm } from "../lib/app-dialogs"
import { usePageLoading } from "../lib/page-loading"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {}),
    },
    ...options,
  })

  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json().catch(() => null) : null

  if (!res.ok) {
    const message =
      data?.message ||
      (data?.error && data.error !== "Not Acceptable" ? String(data.error) : null) ||
      (res.status === 406
        ? "Request was blocked (Not Acceptable). Update the app and retry company registration."
        : `Request failed (${res.status}${res.statusText ? ` ${res.statusText}` : ""})`)
    throw new Error(message)
  }

  return data
}

function formatAuditLine(row) {
  const meta = row.metadata || {}
  switch (row.event_type) {
    case "membership_created": {
      const tpl = meta.role_template_name ? ` (template: ${meta.role_template_name})` : ""
      return `Added ${row.target_name || meta.email} as ${roleLabel(meta.role || meta.new_role || "")}${tpl}`
    }
    case "membership_updated": {
      const tpl = meta.role_template_name ? ` · template: ${meta.role_template_name}` : ""
      return `Changed ${row.target_name || meta.email} from ${roleLabel(meta.previous_role)} to ${roleLabel(meta.new_role)}${tpl}`
    }
    case "membership_removed":
      return `Removed ${row.target_name || meta.email} (${roleLabel(meta.role)})`
    case "access_denied":
      return `Blocked access to ${meta.module || "module"}${meta.section ? ` / ${meta.section}` : ""}`
    case "approval_blocked_sod":
      return `Blocked self-approval of ${meta.document_type || "document"} ${meta.document_number || meta.document_id || ""}`
    case "self_approval_override":
      return `Self-approved ${meta.document_type || "document"} ${meta.document_number || meta.document_id || ""} (Admin/MD override)`
    default:
      return row.event_type
  }
}

function roleLabel(role) {
  const labels = {
    admin: "Admin",
    md: "Managing Director",
    accountant: "Accountant",
    purchase: "Purchase",
    organiser: "Organiser",
    operator: "Operator",
    viewer: "Viewer",
  }
  return labels[role] || role
}

function TextField({ label, value, onChange, type = "text", placeholder, autoComplete }) {
  return (
    <label className="grid gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
      />
    </label>
  )
}

export function AdminPage(props) {
  const {
    user_name,
    current_user_id,
    assignable_roles = [],
    role_catalog = {},
    organization = {},
    access_role,
    can_create_company,
    can_enter_transactions,
    can_approve_documents,
    can_manage_company_settings,
    is_viewer,
  } = props

  const access = accessFromProps(props)

  const [team, setTeam] = useState([])
  const [teamLoading, setTeamLoading] = useState(true)
  const [teamError, setTeamError] = useState(null)

  const [companyName, setCompanyName] = useState("")
  const [companyCurrency, setCompanyCurrency] = useState("INR")
  const [companyGstin, setCompanyGstin] = useState("")
  const [companyState, setCompanyState] = useState("")
  const [companySaving, setCompanySaving] = useState(false)

  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteName, setInviteName] = useState("")
  const [invitePassword, setInvitePassword] = useState("")
  const [invitePasswordConfirm, setInvitePasswordConfirm] = useState("")
  const [inviteLinkUrl, setInviteLinkUrl] = useState("")
  const [inviteLinkSaving, setInviteLinkSaving] = useState(false)
  const [inviteRole, setInviteRole] = useState(assignable_roles[0] || "viewer")
  const [inviteTemplateId, setInviteTemplateId] = useState("")
  const [inviteSaving, setInviteSaving] = useState(false)

  const [banner, setBanner] = useState(null)

  const [auditLogs, setAuditLogs] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  usePageLoading(teamLoading || auditLoading, auditLoading ? "Loading audit…" : "Loading…")

  const [roleTemplates, setRoleTemplates] = useState([])
  const [templateName, setTemplateName] = useState("")
  const [templateBaseRole, setTemplateBaseRole] = useState(assignable_roles[0] || "viewer")
  const [templateModules, setTemplateModules] = useState(["home"])
  const [templateSectionSlugs, setTemplateSectionSlugs] = useState({ home: ["dashboard"] })
  const [templateSaving, setTemplateSaving] = useState(false)
  const [editingTemplateId, setEditingTemplateId] = useState(null)

  const roleDescriptions = role_catalog?.role_descriptions || {}
  const moduleOptions = role_catalog?.module_options || []
  const sectionCatalog = role_catalog?.section_catalog || {}
  const inviteRoleHelp = roleDescriptions[inviteRole] || ""

  const loadTeam = useCallback(async () => {
    setTeamError(null)
    setTeamLoading(true)
    try {
      const rows = await fetchJson("/api/org_team")
      setTeam(Array.isArray(rows) ? rows : [])
    } catch (e) {
      setTeamError(e.message || "Could not load team.")
      setTeam([])
    } finally {
      setTeamLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!organization?.id) {
      setTeamLoading(false)
      setTeam([])
      return
    }
    loadTeam()
  }, [loadTeam, organization?.id])

  const loadAuditLogs = useCallback(async () => {
    if (!can_manage_company_settings) return
    setAuditLoading(true)
    try {
      const rows = await fetchJson("/api/access_audit_logs")
      setAuditLogs(Array.isArray(rows) ? rows : [])
    } catch {
      setAuditLogs([])
    } finally {
      setAuditLoading(false)
    }
  }, [can_manage_company_settings])

  const loadRoleTemplates = useCallback(async () => {
    if (!can_manage_company_settings) return
    try {
      const rows = await fetchJson("/api/organization_role_templates")
      setRoleTemplates(Array.isArray(rows) ? rows : [])
    } catch {
      setRoleTemplates([])
    }
  }, [can_manage_company_settings])

  useEffect(() => {
    if (!organization?.id || !can_manage_company_settings) return
    loadAuditLogs()
    loadRoleTemplates()
  }, [loadAuditLogs, loadRoleTemplates, organization?.id, can_manage_company_settings])

  function defaultSectionsForModule(mod) {
    return (sectionCatalog[mod] || []).map((s) => s.slug)
  }

  function toggleTemplateModule(slug) {
    setTemplateModules((prev) => {
      if (prev.includes(slug)) {
        setTemplateSectionSlugs((secs) => {
          const next = { ...secs }
          delete next[slug]
          return next
        })
        return prev.filter((s) => s !== slug)
      }
      setTemplateSectionSlugs((secs) => ({ ...secs, [slug]: defaultSectionsForModule(slug) }))
      return [...prev, slug]
    })
  }

  function toggleTemplateSection(mod, sectionSlug) {
    setTemplateSectionSlugs((prev) => {
      const current = prev[mod] || []
      const nextList = current.includes(sectionSlug)
        ? current.filter((s) => s !== sectionSlug)
        : [...current, sectionSlug]
      return { ...prev, [mod]: nextList }
    })
  }

  function resetTemplateForm() {
    setEditingTemplateId(null)
    setTemplateName("")
    setTemplateBaseRole(assignable_roles[0] || "viewer")
    setTemplateModules(["home"])
    setTemplateSectionSlugs({ home: ["dashboard"] })
  }

  function startEditTemplate(template) {
    setEditingTemplateId(template.id)
    setTemplateName(template.name || "")
    setTemplateBaseRole(template.base_role || assignable_roles[0] || "viewer")
    const mods = Array.isArray(template.allowed_modules) ? template.allowed_modules : ["home"]
    setTemplateModules(mods.length ? mods : ["home"])
    const sections = template.allowed_sections && typeof template.allowed_sections === "object" ? template.allowed_sections : {}
    setTemplateSectionSlugs(sections)
    setBanner(null)
  }

  async function submitRoleTemplate(e) {
    e.preventDefault()
    if (!templateName.trim() || templateModules.length === 0) {
      setBanner({ type: "error", text: "Template name and at least one module are required." })
      return
    }
    setTemplateSaving(true)
    setBanner(null)
    const wasEditing = Boolean(editingTemplateId)
    const payload = {
      organization_role_template: {
        name: templateName.trim(),
        base_role: templateBaseRole,
        allowed_modules: templateModules,
        allowed_sections: templateSectionSlugs,
      },
    }
    try {
      if (editingTemplateId) {
        await fetchJson(`/api/organization_role_templates/${editingTemplateId}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        })
      } else {
        await fetchJson("/api/organization_role_templates", {
          method: "POST",
          body: JSON.stringify(payload),
        })
      }
      resetTemplateForm()
      await loadRoleTemplates()
      setBanner({
        type: "ok",
        text: wasEditing ? "Role template updated." : "Custom role template created.",
      })
    } catch (err) {
      setBanner({ type: "error", text: err.message || "Could not save template." })
    } finally {
      setTemplateSaving(false)
    }
  }

  async function deleteRoleTemplate(template) {
    if (
      !(await appConfirm(
        `Delete template "${template.name}"? Members using it will fall back to their base role only.`,
        { title: "Delete template", danger: true, confirmLabel: "Delete" }
      ))
    ) {
      return
    }
    setBanner(null)
    try {
      const res = await fetch(`/api/organization_role_templates/${template.id}`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
      })
      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.message || `Request failed (${res.status})`)
      }
      if (editingTemplateId === template.id) resetTemplateForm()
      await loadRoleTemplates()
      await loadTeam()
      setBanner({ type: "ok", text: "Role template deleted." })
    } catch (err) {
      setBanner({ type: "error", text: err.message || "Could not delete template." })
    }
  }

  function exportAuditCsv() {
    window.location.assign("/api/access_audit_logs/export")
  }

  useEffect(() => {
    if (assignable_roles.length && !assignable_roles.includes(inviteRole)) {
      setInviteRole(assignable_roles[0])
    }
  }, [assignable_roles, inviteRole])

  async function submitNewCompany(e) {
    e.preventDefault()
    if (!companyName.trim()) {
      setBanner({ type: "error", text: "Company name is required." })
      return
    }
    setBanner(null)
    setCompanySaving(true)
    try {
      const payload = {
        organization: {
          name: companyName.trim(),
          currency: (companyCurrency || "INR").toString().trim().toUpperCase() || "INR",
        },
      }
      if (companyGstin.trim()) payload.organization.gstin = companyGstin.trim()
      if (companyState.trim()) payload.organization.state_code = companyState.trim()

      await fetchJson("/api/organizations", {
        method: "POST",
        body: JSON.stringify(payload),
      })
      window.location.reload()
    } catch (err) {
      setBanner({ type: "error", text: err.message || "Could not create company." })
    } finally {
      setCompanySaving(false)
    }
  }

  async function submitInviteLink(e) {
    e.preventDefault()
    if (!inviteEmail.trim()) {
      setBanner({ type: "error", text: "Email is required." })
      return
    }
    setBanner(null)
    setInviteLinkSaving(true)
    setInviteLinkUrl("")
    try {
      const body = {
        email: inviteEmail.trim(),
        role: inviteRole,
      }
      if (inviteTemplateId) body.organization_role_template_id = inviteTemplateId
      if (inviteName.trim()) body.name = inviteName.trim()

      const result = await fetchJson("/api/org_team/invitations", {
        method: "POST",
        body: JSON.stringify(body),
      })
      setInviteLinkUrl(result.invite_url || "")
      setBanner({ type: "ok", text: "Invite link created. Copy and share it with your teammate." })
      loadAuditLogs()
    } catch (err) {
      setBanner({ type: "error", text: err.message || "Could not create invite link." })
    } finally {
      setInviteLinkSaving(false)
    }
  }

  async function submitInvite(e) {
    e.preventDefault()
    if (!inviteEmail.trim()) {
      setBanner({ type: "error", text: "Email is required." })
      return
    }
    setBanner(null)
    setInviteSaving(true)
    try {
      const body = {
        email: inviteEmail.trim(),
        role: inviteRole,
      }
      if (inviteTemplateId) body.organization_role_template_id = inviteTemplateId
      if (inviteName.trim()) body.name = inviteName.trim()
      if (invitePassword.trim()) {
        body.password = invitePassword.trim()
        body.password_confirmation = invitePasswordConfirm.trim() || invitePassword.trim()
      }

      const rows = await fetchJson("/api/org_team/users", {
        method: "POST",
        body: JSON.stringify(body),
      })
      setTeam(Array.isArray(rows) ? rows : [])
      setInviteEmail("")
      setInviteName("")
      setInvitePassword("")
      setInvitePasswordConfirm("")
      setBanner({ type: "ok", text: "User added to this company." })
      loadAuditLogs()
    } catch (err) {
      setBanner({ type: "error", text: err.message || "Could not add user." })
    } finally {
      setInviteSaving(false)
    }
  }

  async function changeRole(membershipId, role) {
    setBanner(null)
    try {
      const updated = await fetchJson(`/api/org_team/memberships/${membershipId}`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      })
      setTeam((prev) => prev.map((r) => (r.membership_id === membershipId ? { ...r, ...updated } : r)))
      loadAuditLogs()
    } catch (err) {
      setBanner({ type: "error", text: err.message || "Could not update role." })
    }
  }

  async function changeMemberTemplate(membershipId, templateId, currentRole) {
    setBanner(null)
    try {
      const body = { role: currentRole, organization_role_template_id: templateId || null }
      const updated = await fetchJson(`/api/org_team/memberships/${membershipId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      })
      setTeam((prev) => prev.map((r) => (r.membership_id === membershipId ? { ...r, ...updated } : r)))
      loadAuditLogs()
    } catch (err) {
      setBanner({ type: "error", text: err.message || "Could not update access template." })
    }
  }

  async function removeMember(membershipId) {
    if (!(await appConfirm("Remove this user from the company?", { title: "Remove member", danger: true, confirmLabel: "Remove" }))) return
    setBanner(null)
    try {
      await fetchJson(`/api/org_team/memberships/${membershipId}`, { method: "DELETE" })
      setTeam((prev) => prev.filter((r) => r.membership_id !== membershipId))
      setBanner({ type: "ok", text: "Member removed." })
      loadAuditLogs()
    } catch (err) {
      setBanner({ type: "error", text: err.message || "Could not remove member." })
    }
  }

  const hasOrg = organization != null && organization.id != null
  const orgName = organization?.name || "This company"

  return (
    <BookShell activeSlug="home" userName={user_name} access={access}>
      <div className="flex flex-col gap-6">
        <PageHeader
          title={hasOrg ? "Organization & team" : "Register your company"}
          actions={
            hasOrg ? (
              <span className="text-sm text-muted-foreground">{orgName}</span>
            ) : (
              <span className="text-sm text-muted-foreground">Required before using Books</span>
            )
          }
        />

        {banner ? (
          <div
            className={
              banner.type === "error"
                ? "rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
                : "rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
            }
          >
            {banner.text}
          </div>
        ) : null}

        {can_create_company ? (
          <Card>
            <CardHeader>
              <CardTitle>{hasOrg ? "Create a new company" : "Create your company"}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm text-muted-foreground">
                {hasOrg
                  ? "You will become Admin of the new company. Optional GST fields apply to Indian organizations."
                  : "Enter your business name below. You will be the Admin and can invite your team afterward. Optional GST fields apply to Indian organizations."}
              </p>
              <form className="grid max-w-xl gap-3" onSubmit={submitNewCompany}>
                <TextField label="Company name" value={companyName} onChange={setCompanyName} placeholder="Acme Pvt Ltd" />
                <div className="grid gap-3 sm:grid-cols-3">
                  <CurrencySearchSelect
                    label="Currency"
                    value={CURRENCIES.some((c) => c.code === companyCurrency) ? companyCurrency : "INR"}
                    onChange={setCompanyCurrency}
                  />
                  <TextField label="GSTIN (optional)" value={companyGstin} onChange={setCompanyGstin} placeholder="22AAAAA0000A1Z5" />
                  <TextField label="State code (optional)" value={companyState} onChange={setCompanyState} placeholder="27" />
                </div>
                <div>
                  <Button type="submit" disabled={companySaving}>
                    {companySaving ? "Creating…" : "Create company"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : null}

        {hasOrg ? (
        <Card id="invite-team">
          <CardHeader>
            <CardTitle>Add people to {orgName}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground">
              Add someone directly, or create an invite link they can use to join (valid for 14 days).
            </p>
            <form className="grid max-w-xl gap-3" onSubmit={submitInvite}>
              <TextField label="Email" type="email" value={inviteEmail} onChange={setInviteEmail} autoComplete="email" />
              <TextField label="Display name (optional)" value={inviteName} onChange={setInviteName} autoComplete="name" />
              <div className="grid gap-3 sm:grid-cols-2">
                <TextField
                  label="Password (new users only)"
                  type="password"
                  value={invitePassword}
                  onChange={setInvitePassword}
                  autoComplete="new-password"
                />
                <TextField
                  label="Confirm password"
                  type="password"
                  value={invitePasswordConfirm}
                  onChange={setInvitePasswordConfirm}
                  autoComplete="new-password"
                />
              </div>
              <label className="grid gap-1 text-xs">
                <span className="text-muted-foreground">Role</span>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
                >
                  {assignable_roles.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(r)}
                    </option>
                  ))}
                </select>
                {inviteRoleHelp ? (
                  <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">{inviteRoleHelp}</p>
                ) : null}
              </label>
              {roleTemplates.length > 0 ? (
                <label className="grid gap-1 text-xs">
                  <span className="text-muted-foreground">Custom access template (optional)</span>
                  <select
                    value={inviteTemplateId}
                    onChange={(e) => setInviteTemplateId(e.target.value)}
                    className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm"
                  >
                    <option value="">None — use role defaults only</option>
                    {roleTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={inviteSaving || assignable_roles.length === 0}>
                  {inviteSaving ? "Saving…" : "Add to company"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={inviteLinkSaving || assignable_roles.length === 0}
                  onClick={submitInviteLink}
                >
                  {inviteLinkSaving ? "Creating…" : "Create invite link"}
                </Button>
              </div>
              {inviteLinkUrl ? (
                <div className="grid gap-1">
                  <span className="text-xs text-muted-foreground">Invite link</span>
                  <input
                    type="text"
                    readOnly
                    value={inviteLinkUrl}
                    className="w-full rounded-lg border border-input bg-muted/30 px-2.5 py-1.5 font-mono text-xs"
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              ) : null}
            </form>
          </CardContent>
        </Card>
        ) : null}

        {hasOrg ? (
        <Card>
          <CardHeader>
            <CardTitle>Team</CardTitle>
          </CardHeader>
          <CardContent>
            {teamLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : teamError ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-red-700">{teamError}</span>
                <Button type="button" variant="outline" className="h-8 text-xs" onClick={() => loadTeam()}>
                  Retry
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[800px] border-collapse text-[13px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Role</th>
                      <th className="px-3 py-2">Access template</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {team.map((row) => {
                      const isSelf = Number(row.user_id) === Number(current_user_id)
                      return (
                        <tr key={row.membership_id} className="border-b border-border last:border-0">
                          <td className="px-3 py-2 font-medium text-foreground">{row.name}</td>
                          <td className="px-3 py-2 text-muted-foreground">{row.email}</td>
                          <td className="px-3 py-2">
                            <select
                              value={row.role}
                              disabled={assignable_roles.length === 0}
                              onChange={(e) => changeRole(row.membership_id, e.target.value)}
                              className="max-w-[200px] rounded-md border border-input bg-card px-2 py-1 text-[13px] outline-none focus:border-primary focus:ring-2 focus:ring-ring/20"
                            >
                              {assignable_roles.includes(row.role) ? null : (
                                <option value={row.role}>{roleLabel(row.role)}</option>
                              )}
                              {assignable_roles.map((r) => (
                                <option key={r} value={r}>
                                  {roleLabel(r)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={row.organization_role_template_id || ""}
                              disabled={roleTemplates.length === 0}
                              onChange={(e) =>
                                changeMemberTemplate(row.membership_id, e.target.value, row.role)
                              }
                              className="max-w-[180px] rounded-md border border-input bg-card px-2 py-1 text-[13px] outline-none focus:border-primary focus:ring-2 focus:ring-ring/20"
                            >
                              <option value="">Built-in role only</option>
                              {roleTemplates.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-8 text-xs text-red-700 hover:bg-red-50 hover:text-red-800"
                              disabled={isSelf}
                              onClick={() => removeMember(row.membership_id)}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      )
                    })}
                    {team.length === 0 ? (
                      <tr>
                        <td className="px-3 py-4 text-muted-foreground" colSpan={5}>
                          No members yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        ) : null}

        {hasOrg && can_manage_company_settings ? (
          <Card>
            <CardHeader>
              <CardTitle>Custom role templates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Define module and page access for this company. Assign templates in the team table below. Base role controls enter, approve, and settings permissions.
              </p>
              {roleTemplates.length > 0 ? (
                <ul className="space-y-1 text-[13px]">
                  {roleTemplates.map((t) => (
                    <li
                      key={t.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                    >
                      <div>
                        <span className="font-medium text-foreground">{t.name}</span>
                        <span className="text-muted-foreground">
                          {" "}
                          · base {roleLabel(t.base_role)} · {t.allowed_modules?.join(", ")}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-7 px-2 text-[11px]"
                          onClick={() => startEditTemplate(t)}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-7 px-2 text-[11px] text-red-700 hover:bg-red-50"
                          onClick={() => deleteRoleTemplate(t)}
                        >
                          Delete
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No custom templates yet.</p>
              )}
              <form className="grid max-w-xl gap-3 border-t border-border pt-4" onSubmit={submitRoleTemplate}>
                {editingTemplateId ? (
                  <p className="text-sm font-medium text-foreground">
                    Editing template —{" "}
                    <button
                      type="button"
                      className="text-primary underline-offset-2 hover:underline"
                      onClick={resetTemplateForm}
                    >
                      Cancel
                    </button>
                  </p>
                ) : null}
                <TextField label="Template name" value={templateName} onChange={setTemplateName} placeholder="AP clerk – limited" />
                <label className="grid gap-1 text-xs">
                  <span className="text-muted-foreground">Base role (permissions)</span>
                  <select
                    value={templateBaseRole}
                    onChange={(e) => setTemplateBaseRole(e.target.value)}
                    className="w-full rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm"
                  >
                    {assignable_roles.map((r) => (
                      <option key={r} value={r}>
                        {roleLabel(r)}
                      </option>
                    ))}
                  </select>
                  {roleDescriptions[templateBaseRole] ? (
                    <p className="mt-1 text-[12px] text-muted-foreground">{roleDescriptions[templateBaseRole]}</p>
                  ) : null}
                </label>
                <div className="grid gap-2">
                  <span className="text-xs text-muted-foreground">Allowed modules</span>
                  <div className="flex flex-wrap gap-2">
                    {moduleOptions.map((m) => (
                      <label key={m.slug} className="flex items-center gap-1.5 text-[13px]">
                        <input
                          type="checkbox"
                          checked={templateModules.includes(m.slug)}
                          onChange={() => toggleTemplateModule(m.slug)}
                        />
                        {m.label}
                      </label>
                    ))}
                  </div>
                </div>
                {templateModules.length > 0 ? (
                  <div className="grid gap-3 rounded-lg border border-border bg-muted/20 p-3">
                    <span className="text-xs font-medium text-foreground">Allowed pages (per module)</span>
                    {templateModules.map((mod) => {
                      const sections = sectionCatalog[mod] || []
                      if (sections.length === 0) return null
                      const modLabel = moduleOptions.find((m) => m.slug === mod)?.label || mod
                      return (
                        <div key={mod} className="grid gap-1.5">
                          <span className="text-[12px] font-medium text-muted-foreground">{modLabel}</span>
                          <div className="flex flex-wrap gap-x-3 gap-y-1">
                            {sections.map((sec) => (
                              <label key={sec.slug} className="flex items-center gap-1 text-[12px]">
                                <input
                                  type="checkbox"
                                  checked={(templateSectionSlugs[mod] || []).includes(sec.slug)}
                                  onChange={() => toggleTemplateSection(mod, sec.slug)}
                                />
                                {sec.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" disabled={templateSaving}>
                    {templateSaving ? "Saving…" : editingTemplateId ? "Save changes" : "Create template"}
                  </Button>
                  {editingTemplateId ? (
                    <Button type="button" variant="outline" disabled={templateSaving} onClick={resetTemplateForm}>
                      Cancel
                    </Button>
                  ) : null}
                </div>
              </form>
            </CardContent>
          </Card>
        ) : null}

        {hasOrg && can_manage_company_settings ? (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle>Access audit log</CardTitle>
              <Button type="button" variant="outline" className="h-8 text-xs" onClick={exportAuditCsv}>
                Download CSV
              </Button>
            </CardHeader>
            <CardContent>
              <p className="mb-3 text-sm text-muted-foreground">
                Team changes and blocked page access for this company (last 100 events).
              </p>
              {auditLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : auditLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No events yet.</p>
              ) : (
                <ul className="max-h-64 space-y-2 overflow-y-auto text-[13px]">
                  {auditLogs.map((row) => (
                    <li key={row.id} className="rounded-md border border-border px-3 py-2">
                      <div className="text-foreground">{formatAuditLine(row)}</div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {row.actor_name} · {new Date(row.created_at).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </BookShell>
  )
}
